/**
 * AppsTabContent component
 * Displays O365 app cards for a workspace.
 *
 * Teams and SharePoint link to the MANAGED TENANT (workspace) - these work for B2B guests.
 * Other apps (Outlook, OneDrive, Office apps) link to HOME TENANT resources.
 */

import { useMemo } from 'react';
import { makeStyles, shorthands, tokens, Text, Title3 } from '@fluentui/react-components';
import { RoundedCard } from '../common/RoundedCard';
import { useAuth } from '../../contexts/AuthContext';
import { OutlookIcon } from '../icons/OutlookIcon';
import { TeamsIcon } from '../icons/TeamsIcon';
import { OneDriveIcon } from '../icons/OneDriveIcon';
import { WordIcon } from '../icons/WordIcon';
import { ExcelIcon } from '../icons/ExcelIcon';
import { PowerPointIcon } from '../icons/PowerPointIcon';
import { OneNoteIcon } from '../icons/OneNoteIcon';
import { SharePointIcon } from '../icons/SharePointIcon';
import {
  buildTeamsUrl,
  buildSharePointUrl,
  type WorkspaceContext,
} from '../../utils/workspaceAppUrls';
import type { ReactNode } from 'react';

interface AppInfo {
  name: string;
  url: string;
  icon: ReactNode;
  description: string;
}

/**
 * Extract tenant domain from User Principal Name (UPN).
 * UPN format: user@domain
 */
function getDomainFromUpn(upn: string): string {
  const parts = upn.split('@');
  return parts.length === 2 ? parts[1] : '';
}

/**
 * Extract tenant name from domain for SharePoint URL construction.
 */
function getTenantNameFromDomain(domain: string): string {
  const withoutSuffix = domain.replace('.onmicrosoft.com', '');
  return withoutSuffix.split('.')[0];
}

interface AppRow {
  id: string;
  apps: AppInfo[];
}

interface BuildAppRowsParams {
  workspaceContext: WorkspaceContext;
  userPrincipalName?: string | null;
}

/**
 * Build app definitions organized into rows.
 * Row 1: Teams & SharePoint - Link to MANAGED TENANT (works for B2B guests)
 * Row 2: Outlook & OneDrive - Home tenant resources
 * Row 3: Office apps - Word, Excel, PowerPoint, OneNote
 */
function buildAppRows({ workspaceContext, userPrincipalName }: BuildAppRowsParams): AppRow[] {
  // Teams and SharePoint link to the managed tenant (workspace)
  const teamsUrl = buildTeamsUrl(workspaceContext);
  const managedTenantName = getTenantNameFromDomain(workspaceContext.domain);
  const sharePointUrl = buildSharePointUrl(`https://${managedTenantName}.sharepoint.com`, workspaceContext);

  // OneDrive links to home tenant (from user's UPN)
  let oneDriveUrl = 'https://onedrive.live.com';
  if (userPrincipalName) {
    const homeDomain = getDomainFromUpn(userPrincipalName);
    if (homeDomain) {
      const homeTenantName = getTenantNameFromDomain(homeDomain);
      oneDriveUrl = `https://${homeTenantName}-my.sharepoint.com`;
    }
  }

  return [
    {
      id: 'collaboration',
      apps: [
        {
          name: 'Teams',
          url: teamsUrl,
          icon: <TeamsIcon size={80} />,
          description: 'Chat, meet, call, and collaborate',
        },
        {
          name: 'SharePoint',
          url: sharePointUrl,
          icon: <SharePointIcon size={80} />,
          description: 'Share and manage content, knowledge, and applications',
        },
      ],
    },
    {
      id: 'communication-storage',
      apps: [
        {
          name: 'Outlook',
          url: 'https://outlook.office.com',
          icon: <OutlookIcon size={80} />,
          description: 'Access your email, calendar, and contacts',
        },
        {
          name: 'OneDrive',
          url: oneDriveUrl,
          icon: <OneDriveIcon size={80} />,
          description: 'Store and share your files',
        },
      ],
    },
    {
      id: 'office-apps',
      apps: [
        {
          name: 'Word',
          url: 'https://office.com/launch/word',
          icon: <WordIcon size={80} />,
          description: 'Create and edit documents',
        },
        {
          name: 'Excel',
          url: 'https://office.com/launch/excel',
          icon: <ExcelIcon size={80} />,
          description: 'Create and edit spreadsheets',
        },
        {
          name: 'PowerPoint',
          url: 'https://office.com/launch/powerpoint',
          icon: <PowerPointIcon size={80} />,
          description: 'Create and edit presentations',
        },
        {
          name: 'OneNote',
          url: 'https://office.com/launch/onenote',
          icon: <OneNoteIcon size={80} />,
          description: 'Organize your notes and ideas',
        },
      ],
    },
  ];
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 280px)',
    gap: tokens.spacingHorizontalXL,
    paddingBottom: tokens.spacingVerticalXXL,
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
  },
  middleRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 280px)',
    gap: tokens.spacingHorizontalXL,
  },
  lastRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 280px)',
    gap: tokens.spacingHorizontalXL,
  },
  warningText: {
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  card: {
    height: '100%',
    cursor: 'pointer',
    transitionProperty: 'transform, box-shadow',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      transform: 'translateY(-4px)',
      boxShadow: tokens.shadow16,
    },
  },
  cardLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalXL),
    gap: tokens.spacingVerticalL,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    textAlign: 'center',
  },
  appName: {
    lineHeight: tokens.lineHeightBase300,
  },
  appDescription: {
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase300,
  },
});

interface AppsTabContentProps {
  workspaceId: string;
  workspaceName: string;
  domain: string;
}

export function AppsTabContent({ workspaceId, workspaceName, domain }: AppsTabContentProps) {
  const styles = useStyles();
  const { user } = useAuth();

  const workspaceContext: WorkspaceContext = useMemo(
    () => ({ workspaceId, workspaceName, domain }),
    [workspaceId, workspaceName, domain]
  );

  const appRows = useMemo<AppRow[]>(
    () => buildAppRows({ workspaceContext, userPrincipalName: user?.user_principal_name }),
    [workspaceContext, user?.user_principal_name]
  );

  return (
    <div className={styles.container}>
      <Text className={styles.warningText}>These cards will open in the managed tenant environment</Text>

      {appRows.map((row, index) => (
        <>
          {index === 1 && (
            <Text className={styles.warningText}>These apps open in your home tenant. Microsoft does not support opening them in a managed tenant context for B2B guest users.</Text>
          )}
          <div key={row.id} className={index === 0 ? styles.row : index === 1 ? styles.middleRow : styles.lastRow}>
            {row.apps.map((app) => (
            <RoundedCard key={app.name} className={styles.card}>
              <a
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.cardLink}
              >
                <div className={styles.iconContainer}>
                  {app.icon}
                </div>
                <div className={styles.textContainer}>
                  <Title3 className={styles.appName}>{app.name}</Title3>
                  <Text className={styles.appDescription}>{app.description}</Text>
                </div>
              </a>
            </RoundedCard>
          ))}
          </div>
        </>
      ))}
    </div>
  );
}
